import * as React from 'react';
import { observer } from 'mobx-react';
import { isObservableArray } from 'mobx';
import { EligibilityRule } from 'nomst/Stores/Configuration/eligibilityRule';
import { EligibilityRuleFilter } from 'nomst/Stores/Configuration/eligibilityRuleFilter';
import { RuleCard } from 'nomst/Components/EligibilityProration/ruleCard';
import { FormGroup, Options, Alert, TextArea, Option, Segmented, Dropdown } from '@payscale/design';
import { formatDate, parseDate, getDateLimitForCycle } from 'utils/dateUtils';
import { CustomDatePicker } from 'nomst/Components/Utils/customDatePicker';
import { TagsInputAutocomplete } from '@payscale/design';
import { EligibilityRuleFilters, MonthStrings } from 'typings/enums';
import './eligibilityRuleCard.scss';

interface IEligibilityRuleCardProps {
    rule: EligibilityRule;
    departments: string[];
    workLocations: string[];
    removeRule: (rule: EligibilityRule) => void;
    handleDeleteClick: (rule: EligibilityRule) => void;
    updateOtherRuleAmounts: (rule: EligibilityRule) => void;
}

@observer
export class EligibilityRuleCard extends React.Component<IEligibilityRuleCardProps> {
    constructor(props: IEligibilityRuleCardProps) {
        super(props);

        this.handleBudgetFieldsChange = this.handleBudgetFieldsChange.bind(this);
        this.handleHasHireDateFilterChange = this.handleHasHireDateFilterChange.bind(this);
        this.handleHireDateChange = this.handleHireDateChange.bind(this);
        this.handleHasPayEffectiveFilterChange = this.handleHasPayEffectiveFilterChange.bind(this);
        this.handlePayEffectiveDateChange = this.handlePayEffectiveDateChange.bind(this);
        this.handleCommentChange = this.handleCommentChange.bind(this);
        this.handleDepartmentFilterChange = this.handleDepartmentFilterChange.bind(this);
        this.handleWorkLocationFilterChange = this.handleWorkLocationFilterChange.bind(this);
        this.handleCancelClick = this.handleCancelClick.bind(this);
        this.handleSaveClick = this.handleSaveClick.bind(this);
        this.handleEditClick = this.handleEditClick.bind(this);
        this.handleDeleteClick = this.handleDeleteClick.bind(this);
        this.handleAddDepartmentFilterClick = this.handleAddDepartmentFilterClick.bind(this);
        this.handleRemoveDepartmentFilterClick = this.handleRemoveDepartmentFilterClick.bind(this);
        this.handleAddWorkLocationFilterClick = this.handleAddWorkLocationFilterClick.bind(this);
        this.handleRemoveWorkLocationFilterClick = this.handleRemoveWorkLocationFilterClick.bind(this);
        this.handleAnniversaryMonthChange = this.handleAnniversaryMonthChange.bind(this);
        this.handleAnnualAnniversaryChange = this.handleAnnualAnniversaryChange.bind(this);
        this.searchDepartments = this.searchDepartments.bind(this);
        this.searchWorkLocations = this.searchWorkLocations.bind(this);
    }

    async componentDidMount(): Promise<void> {
        const { rule } = this.props;
        if (!rule.isEditMode) {
            await rule.getEmployeeCount();
        }
    }

    render(): React.ReactNode {
        const { rule } = this.props;
        const employeeCount = rule.isLoadingCount
            ? <i className="icon-animate-spin icon-spin" />
            : rule.employeeCount;
        const body = rule.isEditMode ? this.renderEditRule() : this.renderSavedRule();

        const footer = (
            <>
                <p>Employees within this rule: {employeeCount}</p>
                {this.renderFooterButtons()}
            </>
        );

        return (
            <RuleCard
                mainContent={body}
                footerContent={footer}
                className="nomst-eligibility-rule-card"
            />
        );
    }

    private renderTitle(): React.ReactNode {
        const eligibility = this.props.rule.isAnnual ? 'ineligible' : 'eligible';
        return (
            <h3 className="nomst-eligibility-rule-card__title">
                Mark employees {eligibility} for:
            </h3>
        );
    }

    private renderSavedRule(): React.ReactNode {
        const { rule } = this.props;
        return (
            <div className="nomst-eligibility-rule-card__content nomst-eligibility-rule-card__content--readonly">
                {this.renderTitle()}
                <p className="nomst-eligibility-rule-card__filter">
                    <span className="nomst-eligibility-rule-card__filter-category">
                        {rule.associatedAmountsTitle.join(', ')}
                    </span> Budget
                </p>
                {rule.ruleFilters.map(this.renderSavedRuleFilter)}
                {rule.isAnnual && <>
                    <p className="nomst-eligibility-rule-card__note-to-mgrs">
                        Note to managers:
                    </p>
                    <p className="nomst-eligibility-rule-card__comment">
                        {rule.comment}
                    </p>
                </>}
            </div>
        );
    }

    private renderSavedRuleFilter(ruleFilter: EligibilityRuleFilter): React.ReactNode {
        const categoryClass = 'nomst-eligibility-rule-card__filter-category';
        let filterText: React.ReactNode = null;
        switch (ruleFilter.ruleCategoryName) {
            case EligibilityRuleFilters.HireDate:
            case EligibilityRuleFilters.PayEffectiveDate: {
                const filterCategory = <span className={categoryClass}>
                    {ruleFilter.ruleCategoryName}
                </span>;
                filterText = <>
                    If {filterCategory} is on or after {ruleFilter.ruleCategoryValue}
                </>;
                break;
            }
            case EligibilityRuleFilters.AnniversaryDate: {
                const value = isObservableArray(ruleFilter.ruleCategoryValue)
                    ? ruleFilter.ruleCategoryValue.join(', ')
                    : '';
                const filterValue = <span className={categoryClass}>
                    {value}
                </span>;
                filterText = <>
                    If {ruleFilter.ruleCategoryName} falls within {filterValue}
                </>;
                break;
            }
            case EligibilityRuleFilters.Department:
            case EligibilityRuleFilters.WorkLocation: {
                const value = isObservableArray(ruleFilter.ruleCategoryValue)
                    ? ruleFilter.ruleCategoryValue.join('; ')
                    : '';
                const filterCategory = <span className={categoryClass}>
                    {ruleFilter.ruleCategoryName}
                </span>;
                filterText = <>
                    If {filterCategory} is {value}
                </>;
                break;
            }
        }

        return (
            <p
                key={ruleFilter.ruleCategoryId}
                className="nomst-eligibility-rule-card__filter"
            >
                {filterText}
            </p>
        );
    }

    private renderEditRule(): React.ReactNode {
        const { rule, departments, workLocations } = this.props;
        return (
            <div className="nomst-eligibility-rule-card__content">
                <label
                    className="nomst-eligibility-rule-card__annual-anniversary-label"
                    htmlFor="nomst-eligibility-rule-card__annual-anniversary"
                >
                    Cycle Type:
                </label>
                <Segmented
                    className='pxl-segmented--secondary nomst-eligibility-rule-card__annual-anniversary'
                    value={rule.isAnnual}
                    id='nomst-eligibility-rule-card__annual-anniversary'
                    onChange={this.handleAnnualAnniversaryChange}
                    options={[
                        { 'text': 'Annual', 'value': true },
                        { 'text': 'Anniversary', 'value': false }
                    ]}
                />
                {this.renderTitle()}
                <FormGroup>
                    <label htmlFor="nomst-eligibility-rule-card__budgets">
                        Select Budget:
                    </label>
                    <Options
                        id="nomst-eligibility-rule-card__budgets"
                        options={rule.currentCycleAmountsTitle}
                        onChange={this.handleBudgetFieldsChange}
                        syncOptions={false}
                        value={[...rule.editableAssociatedAmountsTitle]}
                        type="pills"
                        horizontal={true}
                        optionProps={{'className': 'pxl-pill--secondary'}}
                    />
                    {this.renderBudgetAlert()}
                </FormGroup>
                {rule.isAnnual ? this.renderDateFilters() : this.renderAnniversaryMonthFilters()}
                {this.renderFilterMenu()}
                {this.renderFilter(
                    EligibilityRuleFilters.Department,
                    departments.length,
                    this.handleRemoveDepartmentFilterClick,
                    this.handleDepartmentFilterChange,
                    this.searchDepartments
                )}
                {this.renderFilter(
                    EligibilityRuleFilters.WorkLocation,
                    workLocations.length,
                    this.handleRemoveWorkLocationFilterClick,
                    this.handleWorkLocationFilterChange,
                    this.searchWorkLocations
                )}
                {rule.isAnnual && <FormGroup>
                    <label htmlFor="nomst-eligibility-rule-card__comment">
                        Why are employees marked ineligible? (Note: This is visible to managers)
                    </label>
                    <TextArea
                        rows={4}
                        cols={20}
                        id="nomst-eligibility-rule-card__comment"
                        value={rule.editableComment ?? ''}
                        onChange={this.handleCommentChange}
                    />
                </FormGroup>}
            </div>
        );
    }

    private renderAnniversaryMonthFilters(): React.ReactNode {
        const { rule } = this.props;
        const currentMonths = [...rule.getEditableFilterValues(EligibilityRuleFilters.AnniversaryDate)];
        return (
            <FormGroup>
                <label htmlFor="nomst-eligibility-rule-card__anniversary-months">
                    If Anniversary Date falls within:
                </label>
                <Options
                    id="nomst-eligibility-rule-card__anniversary-months"
                    options={[
                        MonthStrings.January,
                        MonthStrings.February,
                        MonthStrings.March,
                        MonthStrings.April,
                        MonthStrings.May,
                        MonthStrings.June,
                        MonthStrings.July,
                        MonthStrings.August,
                        MonthStrings.September,
                        MonthStrings.October,
                        MonthStrings.November,
                        MonthStrings.December
                    ]}
                    onChange={this.handleAnniversaryMonthChange}
                    syncOptions={false}
                    value={currentMonths}
                    type="pills"
                    horizontal={true}
                    optionProps={{ 'className': 'pxl-pill--secondary' }}
                />
            </FormGroup>
        );
    }

    private renderDateFilters(): React.ReactNode {
        const { rule } = this.props;
        const maxDateLimt = getDateLimitForCycle(true, 100);
        const minDateLimit = getDateLimitForCycle(false, 100);
        const hasHireDateFilter = rule.hasRuleFilter(EligibilityRuleFilters.HireDate);
        const hasPayEffectiveDateFilter = rule.hasRuleFilter(EligibilityRuleFilters.PayEffectiveDate);

        return (
            <div className="nomst-eligibility-rule-card__date-filters">
                <FormGroup>
                    <Option
                        id="nomst-eligibility-rule-card__hire-date-checkbox"
                        checked={hasHireDateFilter}
                        onChange={this.handleHasHireDateFilterChange}
                        text="Hire Date"
                        className="pxl-option--secondary"
                    />
                    <CustomDatePicker
                        id="eligibility-hire-date"
                        value={parseDate(rule.getEditableFilterValue(EligibilityRuleFilters.HireDate))}
                        onChange={this.handleHireDateChange}
                        disabled={!hasHireDateFilter}
                        clearIcon={null}
                        maxDateValue={maxDateLimt}
                        minDateValue={minDateLimit}
                    />
                </FormGroup>
                <FormGroup>
                    <Option
                        id="nomst-eligibility-rule-card__pay-effective-checkbox"
                        checked={hasPayEffectiveDateFilter}
                        onChange={this.handleHasPayEffectiveFilterChange}
                        text="Pay Effective Date"
                        className="pxl-option--secondary"
                    />
                    <CustomDatePicker
                        id="eligibility-pay-effective-date"
                        value={parseDate(rule.getEditableFilterValue(EligibilityRuleFilters.PayEffectiveDate))}
                        onChange={this.handlePayEffectiveDateChange}
                        disabled={!hasPayEffectiveDateFilter}
                        clearIcon={null}
                        maxDateValue={maxDateLimt}
                        minDateValue={minDateLimit}
                    />
                </FormGroup>
            </div>
        );
    }

    private renderFilterMenu(): React.ReactNode {
        const { rule } = this.props;

        return (
            <Dropdown
                btnClass="pxl-link nomst-eligibility-rule-card__add-filter-btn"
                btnText="Add Filter"
                iconClass="icon-plus"
                iconWrapperClass="icon-border icon-circle-thin"
                closeOnChildClick={true}
            >
                <button
                    type="button"
                    className="no-btn-style nomst-eligibility-rule-card__filter-btn"
                    disabled={rule.hasRuleFilter(EligibilityRuleFilters.Department)}
                    onClick={this.handleAddDepartmentFilterClick}
                >
                    Department
                </button>
                <button
                    type="button"
                    className="no-btn-style nomst-eligibility-rule-card__filter-btn"
                    disabled={rule.hasRuleFilter(EligibilityRuleFilters.WorkLocation)}
                    onClick={this.handleAddWorkLocationFilterClick}
                >
                    Work Location
                </button>
            </Dropdown>
        );
    }

    private renderFilter(
        filterCategory: EligibilityRuleFilters,
        maxTags: number,
        removeFilterFn: () => Promise<void>,
        onChangeFilterFn: (_: undefined, values: string[]) => Promise<void> | void,
        searchFn: (searchString: string) => Promise<string[]>
    ): React.ReactNode {
        const { rule } = this.props;
        if (!rule.hasRuleFilter(filterCategory)) {
            return null;
        }

        const categoryName = filterCategory.toLowerCase();
        const filterLabel = `If employee's ${categoryName} is:`;
        return (
            <div className={`nomst-eligibility-rule-card__filter-${categoryName.replace(' ', '-')}`}>
                <div className="nomst-eligibility-rule-card__filter-label-remove">
                    <label
                        htmlFor="nomst-eligibility-rule-card__filter-control"
                        className="nomst-eligibility-rule-card__filter-label"
                    >
                        {filterLabel}
                    </label>
                    <button
                        type="button"
                        className="pxl-link nomst-eligibility-rule-card__remove-filter-btn"
                        onClick={removeFilterFn}
                    >
                        Remove
                    </button>
                </div>
                <TagsInputAutocomplete
                    id="nomst-eligibility-rule-card__filter-control"
                    onChange={onChangeFilterFn}
                    fetchData={searchFn}
                    placeholder="Start typing for suggestions"
                    maxTags={maxTags}
                    tagProps={{
                        className: 'pxl-pill pxl-pill--active pxl-pill--secondary'
                    }}
                    url={null}
                    value={[...rule.getEditableFilterValues(filterCategory)]}
                />
            </div>
        );
    }

    private renderBudgetAlert(): React.ReactNode {
        const usedBudgetFields = this.props.rule.alreadyAssociatedWithOtherEligibilityRuleCycleAmounts;
        if (!usedBudgetFields.length) {
            return null;
        }

        const messageText = ' is connected to an existing rule. It will be moved to this one.';
        return usedBudgetFields.map(budgetField => {
            const message = (
                <p>
                    <span className="nomst-eligibility-rule-card__budget-title">
                        {budgetField.title}
                    </span>
                    {messageText}
                </p>
            );

            return (
                <Alert
                    key={budgetField.title}
                    className="nomst-eligibility-rule-card__budget-alert"
                    message={message}
                    level="warning"
                    close={false}
                />
            );
        });
    }

    private renderFooterButtons(): React.ReactNode {
        const { rule } = this.props;
        if (rule.isEditMode) {
            const saveBtnText = rule.isSaving ? 'Saving...' : 'Save';
            return (
                <div className="nomst-eligibility-rule-card__footer-btns">
                    <button
                        className="pxl-link nomst-eligibility-rule-card__cancel-btn"
                        onClick={this.handleCancelClick}
                    >
                        Cancel
                    </button>
                    <button
                        className="pxl-btn pxl-btn--primary nomst-eligibility-rule-card__save-btn"
                        disabled={rule.isEditInvalid || rule.isSaving}
                        onClick={this.handleSaveClick}
                    >
                        {saveBtnText}
                    </button>
                </div>
            );
        }

        return (
            <div className="nomst-eligibility-rule-card__footer-btns">
                <button
                    className="pxl-link nomst-eligibility-rule-card__delete-btn"
                    onClick={this.handleDeleteClick}
                >
                    Delete Rule
                </button>
                <button
                    className="pxl-btn pxl-btn--primary nomst-eligibility-rule-card__edit-btn"
                    onClick={this.handleEditClick}
                >
                    Edit
                </button>
            </div>
        );
    }

    private handleBudgetFieldsChange(_: undefined, value: string[]): void {
        this.props.rule.handleEditableAssociatedAmountsChange(value);
    }

    private async handleHasHireDateFilterChange(): Promise<void> {
        const { rule } = this.props;
        if (rule.hasRuleFilter(EligibilityRuleFilters.HireDate)) {
            await rule.removeRuleFilter(EligibilityRuleFilters.HireDate);
        } else {
            rule.addRuleFilter(EligibilityRuleFilters.HireDate);
        }
    }

    private handleHireDateChange(value: Date): Promise<void> {
        return this.props.rule.setRuleFilterValue(EligibilityRuleFilters.HireDate, formatDate(value));
    }

    private async handleHasPayEffectiveFilterChange(): Promise<void> {
        const { rule } = this.props;
        if (rule.hasRuleFilter(EligibilityRuleFilters.PayEffectiveDate)) {
            await rule.removeRuleFilter(EligibilityRuleFilters.PayEffectiveDate);
        } else {
            rule.addRuleFilter(EligibilityRuleFilters.PayEffectiveDate);
        }
    }

    private handlePayEffectiveDateChange(value: Date): Promise<void> {
        return this.props.rule.setRuleFilterValue(EligibilityRuleFilters.PayEffectiveDate, formatDate(value));
    }

    private handleAddDepartmentFilterClick(): void {
        const { rule } = this.props;
        if (!rule.hasRuleFilter(EligibilityRuleFilters.Department)) {
            rule.addRuleFilter(EligibilityRuleFilters.Department);
        }
    }

    private handleRemoveDepartmentFilterClick(): Promise<void> {
        const { rule } = this.props;
        return rule.removeRuleFilter(EligibilityRuleFilters.Department);
    }

    private handleDepartmentFilterChange(_: undefined, values: string[]): Promise<void> | void {
        const { rule, departments } = this.props;
        const currentDepartments = rule.getEditableFilterValues(EligibilityRuleFilters.Department);
        const value = values[values.length - 1];
        if (values.length > currentDepartments.length && !departments.find(dept => dept === value)) {
            return;
        }

        return rule.setRuleFilterValues(EligibilityRuleFilters.Department, values);
    }

    // Needs to return a promise to work with pxl TagsInputAutocomplete
    private searchDepartments(searchString: string): Promise<string[]> {
        const { departments } = this.props;
        const search = searchString.toLowerCase();
        const results = departments.filter(dept => dept.toLowerCase().includes(search));
        return Promise.resolve(results);
    }

    private handleAddWorkLocationFilterClick(): void {
        const { rule } = this.props;
        if (!rule.hasRuleFilter(EligibilityRuleFilters.WorkLocation)) {
            rule.addRuleFilter(EligibilityRuleFilters.WorkLocation);
        }
    }

    private handleRemoveWorkLocationFilterClick(): Promise<void> {
        const { rule } = this.props;
        return rule.removeRuleFilter(EligibilityRuleFilters.WorkLocation);
    }

    private handleWorkLocationFilterChange(_: undefined, values: string[]): Promise<void> | void {
        const { rule, workLocations } = this.props;
        const currentWorkLocations = rule.getEditableFilterValues(EligibilityRuleFilters.WorkLocation);
        const value = values[values.length - 1];
        if (values.length > currentWorkLocations.length && !workLocations.find(dept => dept === value)) {
            return;
        }

        return rule.setRuleFilterValues(EligibilityRuleFilters.WorkLocation, values);
    }

    // Needs to return a promise to work with pxl TagsInputAutocomplete
    private searchWorkLocations(searchString: string): Promise<string[]> {
        const { workLocations } = this.props;
        const search = searchString.toLowerCase();
        const results = workLocations.filter(location => location.toLowerCase().includes(search));
        return Promise.resolve(results);
    }

    private handleCommentChange(_: undefined, value: string): void {
        this.props.rule.setEditableComment(value);
    }

    private async handleCancelClick(): Promise<void> {
        const { rule, removeRule } = this.props;
        if (!rule.eligibilityRuleId) {
            removeRule(rule);
        } else {
            await rule.cancelEditRule();
        }
    }

    private async handleSaveClick(): Promise<void> {
        const { rule, updateOtherRuleAmounts } = this.props;
        await rule.saveChanges();
        updateOtherRuleAmounts(rule);
    }

    private handleEditClick(): void {
        this.props.rule.editRule();
    }

    private async handleDeleteClick(): Promise<void> {
        const { handleDeleteClick, rule } = this.props;
        handleDeleteClick(rule);
    }

    private handleAnnualAnniversaryChange(_: undefined, value: boolean): Promise<void> {
        const { rule } = this.props;
        return rule.toggleRuleType(value);
    }

    private handleAnniversaryMonthChange(_: undefined, value: string[]): Promise<void> {
        const { rule } = this.props;
        return rule.setRuleFilterValues(EligibilityRuleFilters.AnniversaryDate, value);
    }
}

export default { EligibilityRuleCard };